import { getAbTestingData } from '@/api/public';
import { IS_US } from '@/utils/constants';
import type {
  AppliedOfferSource,
  AutoOfferApiParams,
  AutoOfferEnableInput,
  AutoOfferStore,
  NormalizedAppliedOffer,
} from './types';

export type {
  AppliedOfferSource,
  AutoOfferApiParams,
  AutoOfferCouponMode,
  AutoOfferEnableInput,
  AutoOfferPointMode,
  AutoOfferStore,
  NormalizedAppliedOffer,
} from './types';

type AutoOfferGroupState = {
  enabled: boolean;
  abValue: number | string | null;
};

/**
 * Cart_Discounts_Auto_Apply_Test = 2 的时候才会应用自动
 * 对照组 - 用户手动 - 应用优惠50% - 1
 * 试验组1 - 系统自动应用 - 优惠50% - 2
 */
const AUTO_OFFER_MANUAL_APPLY_VALUE = 1;
const AUTO_OFFER_AUTO_APPLY_VALUE = 2;
export const PAYMENT_AUTO_OFFER_TOAST_STORAGE_KEY = 'zl-payment-auto-offer-toast';

export const normalizeAmount = (amount?: string | number | null) => {
  if (amount === null || amount === undefined || amount === '') {
    return 0;
  }
  const value = Number(String(amount).replace(/,/g, ''));
  return Number.isFinite(value) ? value : 0;
};

export const shouldEnableManualOffer = ({ selectedCount = 0 }: AutoOfferEnableInput) =>
  IS_US && selectedCount > 0;

const shouldAutoApplyOffer = (state?: AutoOfferStore | AutoOfferGroupState) =>
  Boolean(state?.enabled) && Number(state?.abValue) === AUTO_OFFER_AUTO_APPLY_VALUE;

const shouldManualApplyOffer = (state?: AutoOfferStore | AutoOfferGroupState) =>
  Boolean(state?.enabled) && Number(state?.abValue) === AUTO_OFFER_MANUAL_APPLY_VALUE;

/**
 * 改动的时候都需要复测这两个case
 * https://devops.aliyun.com/projex/workitem#viewIdentifier=441e17ad4f72718076eedcf5&openWorkitemIdentifier=7ce93e02d5e745d7a18bcc3149
 * https://devops.aliyun.com/projex/project/ce85697916dd382a20b8106373/bug#viewIdentifier=1dedc5afd44979211cad516f&openWorkitemIdentifier=36a745c0a1060cab4a20ecec87
 */
export const shouldRestoreAutoOfferOnCartChange = (state?: AutoOfferStore | null) =>
  IS_US &&
  Number(state?.abValue) === AUTO_OFFER_AUTO_APPLY_VALUE &&
  (['manual_coupon', 'manual_code', 'skip'].includes(state?.couponMode) ||
    state?.pointMode !== 'auto');

const autoGroup: Pick<AutoOfferStore, 'couponMode' | 'pointMode'> = {
  couponMode: 'auto',
  pointMode: 'auto',
};

const manualGroup: Pick<AutoOfferStore, 'couponMode' | 'pointMode'> = {
  couponMode: 'auto',
  pointMode: 'none',
};

export const clearAutoOfferCouponSelection = (state: AutoOfferStore): AutoOfferStore => ({
  ...state,
  coupon: undefined,
  code: '',
  couponText: '',
  ...(shouldManualApplyOffer(state) ? manualGroup : autoGroup),
});

export const clearFailedCouponSelection = (state: AutoOfferStore): AutoOfferStore => {
  const hasManualCouponSelection = ['manual_coupon', 'manual_code'].includes(state.couponMode);
  const hasRestoredCodeSelection = state.couponMode === 'auto' && Boolean(state.code);

  if (!hasManualCouponSelection && !hasRestoredCodeSelection) {
    return state;
  }

  return clearAutoOfferCouponSelection(state);
};

export const resolveCartOfferAfterPricingFailure = (state: AutoOfferStore): AutoOfferStore => ({
  ...clearFailedCouponSelection(state),
  applied_offer: normalizeAppliedOffer(undefined),
});

const getAppliedManualCode = (state: AutoOfferStore) => {
  if (
    state.couponMode === 'manual_code' &&
    state.applied_offer?.applied &&
    state.applied_offer.coupon.type === 'code'
  ) {
    return state.code || state.applied_offer.coupon.code || '';
  }

  return '';
};

export const restoreAutoOfferState = (state: AutoOfferStore): AutoOfferStore => ({
  ...clearAutoOfferCouponSelection(state),
  code: getAppliedManualCode(state),
});

export const resolveCartOfferForLoginCompare = restoreAutoOfferState;

/**
 * 留在购物车登录后的计价输入解析。
 *
 * 游客已成功应用的手动 code 需要参与登录后的最优优惠比较，所以这里把
 * manual_code 转成 auto + code。其他手动 coupon/skip 不在这个 helper
 * 中静默归一化，避免把一次用户选择误当成通用计价收口规则。
 */
export const resolveCartOfferForPricing = (
  selection?: AutoOfferStore | null,
): AutoOfferStore | null | undefined => {
  if (!selection || !shouldAutoApplyOffer(selection) || !getAppliedManualCode(selection)) {
    return selection;
  }

  return resolveCartOfferForLoginCompare(selection);
};

export const createDefaultAutoOfferStore = (
  state?: Partial<Pick<AutoOfferStore, 'enabled' | 'abValue'>>,
): AutoOfferStore => {
  const enabled = state?.enabled ?? IS_US;
  const abValue = state?.abValue ?? null;

  return {
    enabled,
    abValue,
    code: '',
    expendPoint: undefined,
    applied_offer: normalizeAppliedOffer(undefined),
    coupon: undefined,
    ...(shouldManualApplyOffer({ enabled, abValue }) ? manualGroup : autoGroup),
  };
};

export const hasExplicitAutoOfferSelection = (state?: AutoOfferStore | null) =>
  Boolean(state && (state.couponMode !== 'auto' || state.pointMode !== 'auto'));

export const normalizeAppliedOffer = (raw: any): NormalizedAppliedOffer => ({
  enabled: Boolean(raw?.enabled),
  applied: Boolean(raw?.applied),
  autoApplied: Boolean(raw?.auto_applied),
  show_best_offer_text: Boolean(raw?.show_best_offer_text),
  coupon: {
    source: (raw?.coupon?.source || 'none') as AppliedOfferSource,
    is_best: Boolean(raw?.coupon?.is_best),
    code: raw?.coupon?.code || '',
    detail: raw?.coupon?.detail || {},
    type: raw?.coupon?.type || '',
  },
  point: {
    applied: Boolean(raw?.point?.applied),
    source: (raw?.point?.source || 'none') as AppliedOfferSource,
    availablePoint: Number(raw?.point?.available_point || 0),
    maxExpendPoint: Number(raw?.point?.max_expend_point || 0),
    expendPoint: Number(raw?.point?.expend_point || 0),
  },
  reason: raw?.reason || '',
});

/**
 * 接口文档
 * https://alidocs.dingtalk.com/i/nodes/QG53mjyd80R5bQdxUg3G49M1V6zbX04v
 */
export const buildCartAutoOfferParams = (state?: AutoOfferStore | null): AutoOfferApiParams => {
  if (!state) {
    return {};
  }

  const params: AutoOfferApiParams = {};

  if (!IS_US) {
    return params;
  } else {
    /**
     * 开启自动优惠的开关，A 侧手动也需要这个才能应用自动优惠。
     */
    params.auto_apply_offer = 1;
  }

  switch (state.couponMode) {
    case 'auto':
      if (shouldAutoApplyOffer(state)) {
        params.auto_coupon_compare = 1;
        if (state.code) {
          params.code = state.code;
        }
      }
      break;

    case 'skip':
      params.skip_coupon = 1;
      break;

    case 'manual_coupon':
      if (state.coupon) {
        params.coupon = state.coupon;
      }
      break;

    case 'manual_code':
      if (state.code) {
        params.code = state.code;
      }
      break;
  }

  if (state.pointMode === 'none') {
    params.point = 0;
  } else if (state.pointMode === 'manual' && Number(state.expendPoint) > 0) {
    params.point = 1;
  }

  return params;
};

export const shouldUseBestOfferOnCartEnter = shouldRestoreAutoOfferOnCartChange;

export const buildCartEnterAutoOfferParams = (
  state?: AutoOfferStore | null,
): AutoOfferApiParams => {
  if (!shouldUseBestOfferOnCartEnter(state)) {
    return buildCartAutoOfferParams(state);
  }

  return buildCartAutoOfferParams({
    ...state,
    couponMode: 'auto',
    pointMode: 'auto',
    coupon: undefined,
    code: '',
    couponText: '',
  });
};

export const mergeAppliedOfferIntoState = (
  state: AutoOfferStore,
  rawAppliedOffer: any,
): AutoOfferStore => {
  const applied_offer = normalizeAppliedOffer(rawAppliedOffer);

  return {
    ...state,
    applied_offer,
  };
};

/**
 * 文档：
 * https://alidocs.dingtalk.com/i/nodes/dpYLaezmVNLZdaXbFGQDp5AP8rMqPxX6?utm_scene=team_space
 * 验证自动应用最佳优惠对购物车结算、订单创建的影响（PC+M）
 * 进组节点：进入购物车
 * 对照组: 用户手动应用优惠
 * 试验组1: 系统自动应用优惠
 */
export const fetchCartAutoOfferAbValue = async () => {
  const AUTO_OFFER_EXPERIMENT_KEY = 'Cart_Discounts_Auto_Apply_Test';
  const { code, data } = await getAbTestingData({
    param_name: AUTO_OFFER_EXPERIMENT_KEY,
  });

  if (code === 200) {
    const abValue = Number(data?.strategy_variable);
    if (Number.isFinite(abValue)) {
      return abValue;
    }
  }
  // 兜底默认选中
  return 1;
};
